import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useToast } from 'src/shared/hooks/use-toast'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'
import { convertFromSEK } from 'src/shared/utils/currency'
import { Database } from 'supabase/database.types'
import { queryKeys } from './queryKeys'

export type InsertProduct = Database['public']['Tables']['product']['Insert']
export type Product_ = Database['public']['Tables']['product']['Row'] & {
  product_price: number
  product_variant?: (Database['public']['Tables']['product_variant']['Row'] & {
    product_variant_color?: (Database['public']['Tables']['product_variant_color']['Row'] & {
      product_image?: Database['public']['Tables']['product_image']['Row'][]
    })[]
  })[]
}
// btw jag saknar varianter i db

export const useProductsClosedApi = ({
  product_id,
  page = 1,
  pageSize = 25,
  searchArticleNumber,
  searchItemName,
  searchSource,
  searchCategory,
  klayder_recommends,
  withoutCategories,
  withoutTranslations,
  isDraft,
}: {
  product_id?: number
  page?: number
  pageSize?: number
  searchArticleNumber?: string
  searchItemName?: string
  searchSource?: string
  searchCategory?: string
  klayder_recommends?: boolean
  withoutCategories?: boolean
  withoutTranslations?: boolean
  isDraft?: boolean
}) => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()
  const { toast } = useToast()
  const currency = useOrganizationCurrency()

  // Only enable query if at least one search parameter or product_id is provided
  // This prevents fetching all products when no search criteria is given
  // Note: klayder_recommends can be explicitly false, so check if it's not undefined
  const hasSearchCriteria =
    !!searchArticleNumber ||
    !!searchItemName ||
    !!searchSource ||
    !!searchCategory ||
    klayder_recommends !== undefined ||
    withoutCategories ||
    withoutTranslations ||
    isDraft ||
    !!product_id

  const { data: productsClosed, isFetching: isProductsFetching } = useQuery({
    queryKey: [
      queryKeys.productsClosed,
      page,
      pageSize,
      searchArticleNumber,
      searchItemName,
      searchSource,
      searchCategory,
      klayder_recommends,
      withoutCategories,
      withoutTranslations,
      isDraft,
      product_id,
      currency,
    ].filter((v) => v !== undefined),
    enabled: hasSearchCriteria,
    queryFn: async () => {
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1

      // If searching by category, first find matching category IDs and get product IDs
      let productIdsFromCategory: number[] | undefined
      if (searchCategory) {
        const { data: categories, error: categoryError } = await supabase
          .from('category')
          .select('id')
          .ilike('title', `%${searchCategory}%`)

        if (categoryError) throw categoryError

        if (categories && categories.length > 0) {
          const categoryIds = categories.map((c) => c.id)

          // Get product IDs from category_product table
          const { data: categoryProducts, error: cpError } = await supabase
            .from('category_product')
            .select('product_id')
            .in('category_id', categoryIds)

          if (cpError) throw cpError

          if (categoryProducts && categoryProducts.length > 0) {
            productIdsFromCategory = categoryProducts
              .map((cp) => cp.product_id)
              .filter((id): id is number => id !== null)
          } else {
            // No products in matching categories, return empty result
            return {
              data: [],
              count: 0,
              page,
              pageSize,
              totalPages: 0,
            }
          }
        } else {
          // No matching categories, return empty result
          return {
            data: [],
            count: 0,
            page,
            pageSize,
            totalPages: 0,
          }
        }
      }

      let query = supabase.from('product').select(
        `id,
          article_number,
          is_draft,
          fast_track,
          has_variants,
          moq,
          margin,
          cost,
          item_name,
          item_name_translations,
          item_description,
          item_description_translations,
          packaging_cost,
          print_codes,
          created_at,
          source,
          price,
          klayder_recommendation_text,
          klayder_recommends,
          related_product_ids,

          category_product(*, category(*))`,
        { count: 'exact' }
      )

      // Apply search filters
      // If both searchArticleNumber and searchItemName are provided, combine them with OR logic
      if (searchArticleNumber && searchItemName) {
        query = query.or(
          `article_number.ilike.%${searchArticleNumber}%, item_name_translations.ilike.%${searchItemName}%, item_description_translations.ilike.%${searchItemName}%`
        )
      } else if (searchArticleNumber) {
        query = query.ilike('article_number', `%${searchArticleNumber}%`)
      } else if (searchItemName) {
        query = query.or(
          `item_name_translations.ilike.%${searchItemName}%, item_description_translations.ilike.%${searchItemName}%`
        )
      }
      if (searchSource) {
        query = query.ilike('source', `%${searchSource}%`)
      }
      // Apply product ID filter when searching by category
      let effectiveProductIds: number[] | undefined
      if (
        searchCategory &&
        productIdsFromCategory &&
        productIdsFromCategory.length > 0
      ) {
        effectiveProductIds = productIdsFromCategory
      }

      if (effectiveProductIds && effectiveProductIds.length > 0) {
        query = query.in('id', effectiveProductIds)
      }
      if (klayder_recommends) {
        query = query.eq('klayder_recommends', klayder_recommends)
      }
      if (isDraft) {
        query = query.eq('is_draft', true)
      }
      if (product_id) {
        query = query.eq('id', product_id)
      }

      const { data, count, error } = await query
        .range(from, to)
        .order('created_at', { ascending: false })

      if (error) throw error

      // Filter products without categories if requested
      let filteredData = data || []
      if (withoutCategories && filteredData.length > 0) {
        filteredData = filteredData.filter(
          (p) => !p.category_product || p.category_product.length === 0
        )
      }

      // Filter products without translations if requested
      if (withoutTranslations && filteredData.length > 0) {
        filteredData = filteredData.filter((p) => !p.item_name_translations)
      }

      // Fetch product_variants separately for the returned products to avoid timeout
      // This is more efficient than joining nested relationships in the main query
      let productsWithRelations = filteredData
      if (filteredData && filteredData.length > 0) {
        const productIds = filteredData
          .map((p) => p.id)
          .filter((id): id is number => id !== null)

        if (productIds.length > 0) {
          const { data: variantsData, error: variantsError } = await supabase
            .from('product_variant')
            .select('*, product_variant_color(*, product_image(id, image_url))')
            .in('product_id', productIds)

          if (variantsError) {
            console.error('Error fetching variants:', variantsError)
          }

          // Group variants by product_id
          const variantsByProductId =
            variantsData?.reduce(
              (acc, variant) => {
                const productId = variant.product_id
                if (productId !== null) {
                  if (!acc[productId]) {
                    acc[productId] = []
                  }
                  acc[productId].push(variant)
                }
                return acc
              },
              {} as Record<number, typeof variantsData>
            ) || {}

          // Merge variants into products
          productsWithRelations = filteredData.map((product) => ({
            ...product,
            product_variant:
              (product.id !== null
                ? variantsByProductId[product.id]
                : undefined) || [],
          }))
        }
      }

      // Convert prices/costs from SEK to target currency
      productsWithRelations = productsWithRelations.map((product) => ({
        ...product,
        cost: convertFromSEK(product.cost, currency) ?? product.cost,
        price: convertFromSEK(product.price, currency) ?? product.price,
        packaging_cost:
          convertFromSEK(product.packaging_cost, currency) ??
          product.packaging_cost,
      }))

      // When filtering by withoutCategories or withoutTranslations, we need to use the filtered count
      const finalCount =
        withoutCategories || withoutTranslations
          ? productsWithRelations.length
          : count

      return {
        data: productsWithRelations,
        count: finalCount,
        page,
        pageSize,
        totalPages: finalCount ? Math.ceil(finalCount / pageSize) : 0,
      }
    },
  })

  const createProduct = useMutation({
    mutationFn: async (insertProduct: InsertProduct) =>
      await supabase
        .from('product')
        .insert(insertProduct)
        .select('id')
        .single(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.productsClosed] })
      return data
    },
  })

  const updateProduct = useMutation({
    mutationFn: async (insertProduct: InsertProduct) =>
      await supabase
        .from('product')
        .update(insertProduct)
        .eq('id', insertProduct.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.productsClosed] })
      toast({
        variant: 'success',
        title: 'Updated product',
      })
    },
  })

  const deleteProduct = useMutation({
    mutationFn: async (id: number) =>
      await supabase.from('product').delete().eq('id', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.productsClosed] })
    },
  })

  return {
    productsClosed,
    isProductsFetching,
    createProduct,
    updateProduct,
    deleteProduct,
  }
}
